import { Component, Project, Page } from "@/types";
import { logger } from "@/utils";

export interface ComponentPersistenceReport {
  projectId: string;
  projectName: string;
  timestamp: Date;
  validation: {
    totalPages: number;
    totalComponents: number;
    pagesWithComponents: number;
    componentValidation: {
      allHaveIds: boolean;
      allHaveTypes: boolean;
      allHaveValidPositions: boolean;
      allHaveValidSizes: boolean;
    };
    persistenceStrategy: string;
    warnings: string[];
    errors: string[];
  };
}

/**
 * Validates component persistence and data integrity
 */
export class ComponentPersistenceValidator {
  static validateProject(project: Project, strategy: string = 'unknown'): ComponentPersistenceReport {
    const timestamp = new Date();
    const warnings: string[] = [];
    const errors: string[] = [];

    // Extract components from various sources
    let totalComponents = 0;
    let pagesWithComponents = 0;
    let allComponents: Component[] = [];

    // Count from project.pages
    if (project.pages && Array.isArray(project.pages)) {
      project.pages.forEach((page) => {
        // Type assertion to handle potential components property on pages
        const pageWithComponents = page as Page & { components?: Component[] };
        if (pageWithComponents.components && Array.isArray(pageWithComponents.components)) {
          totalComponents += pageWithComponents.components.length;
          if (pageWithComponents.components.length > 0) {
            pagesWithComponents++;
          }
          allComponents.push(...pageWithComponents.components);
        }
      });
    }

    // Count from project.components (legacy - may exist in imported data)
    const legacyComponents = (project as Project & { components?: Component[] }).components;
    if (legacyComponents && Array.isArray(legacyComponents)) {
      totalComponents += legacyComponents.length;
      allComponents.push(...legacyComponents);
    }

    // Validate component data integrity
    const componentValidation = {
      allHaveIds: allComponents.every(comp => comp && comp.id && typeof comp.id === 'string'),
      allHaveTypes: allComponents.every(comp => comp && comp.type && typeof comp.type === 'string'),
      allHaveValidPositions: allComponents.every(comp =>
        comp && comp.position &&
        typeof comp.position.x === 'number' &&
        typeof comp.position.y === 'number'
      ),
      allHaveValidSizes: allComponents.every(comp =>
        comp && comp.size &&
        typeof comp.size.width === 'number' &&
        typeof comp.size.height === 'number'
      ),
    };

    // Generate warnings
    if (totalComponents === 0) {
      warnings.push("No components found in project");
    }

    if (!componentValidation.allHaveIds) {
      errors.push("Some components missing valid IDs");
    }

    if (!componentValidation.allHaveTypes) {
      errors.push("Some components missing valid types");
    }

    if (!componentValidation.allHaveValidPositions) {
      warnings.push("Some components have invalid positions");
    }

    if (!componentValidation.allHaveValidSizes) {
      warnings.push("Some components have invalid sizes");
    }

    const report: ComponentPersistenceReport = {
      projectId: project.id,
      projectName: project.name,
      timestamp,
      validation: {
        totalPages: project.pages?.length || 0,
        totalComponents,
        pagesWithComponents,
        componentValidation,
        persistenceStrategy: strategy,
        warnings,
        errors,
      },
    };

    // Log the validation report
    logger.info("Component persistence validation completed", {
      projectId: project.id,
      projectName: project.name,
      strategy,
      totalComponents,
      hasErrors: errors.length > 0,
      hasWarnings: warnings.length > 0,
      report,
    });

    return report;
  }

  /**
   * Validates components during the import process
   */
  static validateImport(importData: { pages?: Array<{ name?: string; components?: unknown[] }>; [key: string]: unknown }): { isValid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!importData.pages || !Array.isArray(importData.pages)) {
      errors.push("Import data missing valid pages array");
      return { isValid: false, errors, warnings };
    }

    let totalComponents = 0;

    importData.pages.forEach((page, pageIndex: number) => {
      const typedPage = page as { name?: string; components?: unknown[] };

      if (!typedPage.name) {
        errors.push(`Page ${pageIndex} missing name`);
      }

      if (typedPage.components) {
        if (!Array.isArray(typedPage.components)) {
          errors.push(`Page ${pageIndex} components is not an array`);
        } else {
          totalComponents += typedPage.components.length;

          typedPage.components.forEach((comp, compIndex: number) => {
            const typedComp = comp as { id?: string; type?: string; position?: { x?: number; y?: number } };

            if (!typedComp.id) {
              errors.push(`Page ${pageIndex}, Component ${compIndex} missing ID`);
            }

            if (!typedComp.type) {
              errors.push(`Page ${pageIndex}, Component ${compIndex} missing type`);
            }

            if (!typedComp.position || typeof typedComp.position.x !== 'number' || typeof typedComp.position.y !== 'number') {
              warnings.push(`Page ${pageIndex}, Component ${compIndex} has invalid position`);
            }
          });
        }
      }
    });

    if (totalComponents === 0) {
      warnings.push("Import contains no components");
    }

    logger.info("Import validation completed", {
      totalComponents,
      pageCount: importData.pages.length,
      errorCount: errors.length,
      warningCount: warnings.length,
      isValid: errors.length === 0,
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Compares two component states to identify differences
   */
  static compareStates(before: Component[], after: Component[], context: string): void {
    const beforeIds = new Set(before.map(c => c.id));
    const afterIds = new Set(after.map(c => c.id));

    const added = after.filter(c => !beforeIds.has(c.id));
    const removed = before.filter(c => !afterIds.has(c.id));
    const preserved = after.filter(c => beforeIds.has(c.id));

    logger.info(`Component state comparison (${context})`, {
      context,
      before: {
        count: before.length,
        ids: Array.from(beforeIds),
      },
      after: {
        count: after.length,
        ids: Array.from(afterIds),
      },
      changes: {
        added: added.length,
        removed: removed.length,
        preserved: preserved.length,
        addedIds: added.map(c => c.id),
        removedIds: removed.map(c => c.id),
      },
    });

    if (removed.length > 0) {
      logger.warn(`Components lost during ${context}`, {
        removedCount: removed.length,
        removedComponents: removed.map(c => ({ id: c.id, type: c.type, name: c.name })),
      });
    }
  }
}
